// Additional homepage sections
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Building2, Coins, Landmark, Zap, Globe, TrendingUp,
  Fingerprint, ScrollText, Lock, Radio, ShieldCheck, CheckCircle2,
  BarChart3, Users, Layers as LayersIcon, Wallet, ArrowUpRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, AnimatedCounter } from "@/components/shared";

const fmt = (n, d = 0) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

// =====================================================
//  LIVE OFFERINGS SHOWCASE
// =====================================================
export function LiveOfferings() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/offerings").then((r) => setItems((r.data || []).filter((o) => o.status === "OPEN").slice(0, 3))).catch(() => {});
  }, []);
  if (items.length === 0) return null;
  return (
    <section className="py-20 md:py-28 relative">
      <div className="absolute -top-32 left-1/4 w-[600px] h-[400px] blue-glow pointer-events-none" />
      <div className="container-auvra relative z-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <Reveal><span className="kicker mb-3 block">Live Marketplace</span></Reveal>
            <Reveal i={1}>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-[1.05]">
                Curated tokenized offerings — <span className="text-auvra-blue">open now</span>
              </h2>
            </Reveal>
            <Reveal i={2}>
              <p className="mt-4 text-secondary-auvra text-base md:text-lg leading-relaxed">Real yield from real assets. Fractional access from $100, on-chain distributions and full transparency.</p>
            </Reveal>
          </div>
          <Reveal i={2}>
            <Link to="/investors" className="inline-flex items-center gap-2 border border-white/15 hover:border-auvra-blue text-white font-medium px-5 py-2.5 rounded-full text-sm transition-all">
              View all offerings <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-4" data-testid="home-offerings">
          {items.map((o, i) => (
            <Reveal key={o.id} i={i}>
              <Link to="/investors" className="group block glass rounded-2xl overflow-hidden hover:border-auvra-blue/40 transition-colors h-full">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={o.image} alt={o.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-green-500/10 border-green-500/30 text-green-400">OPEN</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-white/5 border-white/15 text-white">{o.class}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold tracking-tight">{o.title}</h3>
                  <p className="text-sm text-secondary-auvra mt-2 line-clamp-2">{o.summary}</p>
                  <div className="grid grid-cols-3 gap-3 mt-5 text-xs">
                    <div><div className="text-secondary-auvra">Yield</div><div className="text-auvra-blue text-base font-semibold">{o.target_yield}%</div></div>
                    <div><div className="text-secondary-auvra">Term</div><div className="text-base">{o.term_months}m</div></div>
                    <div><div className="text-secondary-auvra">Min</div><div className="text-base">${fmt(o.min_ticket)}</div></div>
                  </div>
                  <div className="mt-4">
                    <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-auvra-blue" style={{ width: `${Math.min(100, (o.raised / o.target) * 100)}%` }} />
                    </div>
                    <div className="text-[10px] text-secondary-auvra mt-1 flex justify-between"><span>${fmt(o.raised / 1e6, 2)}M raised</span><span>${fmt(o.target / 1e6, 1)}M target</span></div>
                  </div>
                  <div className="mt-4 flex items-center gap-1 text-sm text-auvra-blue">Explore <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// =====================================================
//  ASSET CLASSES GRID
// =====================================================
export function AssetClassesGrid() {
  const CLASSES = [
    { I: Building2, t: "Real Estate", d: "Prime residential, commercial and hospitality assets — income-producing, independently valued." },
    { I: Coins, t: "Private Credit", d: "Senior secured lending to mid-market operators. Quarterly income, low correlation." },
    { I: Landmark, t: "Fixed Income", d: "Short-duration treasury baskets and structured credit with daily NAV and on-chain redemption." },
    { I: Zap, t: "Infrastructure", d: "Utility-scale solar, transport and digital infrastructure with long-dated PPA revenues." },
    { I: Globe, t: "Commodities", d: "Rules-based baskets across energy, agriculture and metals with weekly rebalancing." },
    { I: TrendingUp, t: "Venture & Growth", d: "Curated early-stage exposure with periodic secondary liquidity windows." },
  ];
  return (
    <section className="py-20 md:py-28" id="asset-classes">
      <div className="container-auvra">
        <div className="max-w-2xl mb-12">
          <Reveal><span className="kicker mb-3 block">Investable Universes</span></Reveal>
          <Reveal i={1}>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-[1.05]">
              Six asset classes.<br /><span className="text-auvra-blue">One institutional rail.</span>
            </h2>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CLASSES.map((c, i) => (
            <Reveal key={c.t} i={i % 3}>
              <div className="glass rounded-2xl p-8 h-full hover:border-auvra-blue/30 transition-colors group">
                <div className="w-12 h-12 rounded-xl bg-auvra-blue/10 border border-auvra-blue/20 flex items-center justify-center mb-5">
                  <c.I className="w-5 h-5 text-auvra-blue" />
                </div>
                <h3 className="text-lg font-semibold tracking-tight">{c.t}</h3>
                <p className="text-sm text-secondary-auvra mt-2 leading-relaxed">{c.d}</p>
                <Link to="/tokenized-assets" className="mt-5 inline-flex items-center gap-1 text-sm text-auvra-blue">
                  Explore <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// =====================================================
//  PLATFORM LIVE METRICS
// =====================================================
export function PlatformMetrics() {
  const M = [
    { v: 47.2, s: "M+", label: "Assets Tokenized ($USD)", icon: Wallet },
    { v: 2140, s: "+", label: "On-chain Investors", icon: Users },
    { v: 1.4, s: "M+", label: "Distributions Paid ($USD)", icon: Coins },
    { v: 8.6, s: "%", label: "Blended Target Yield", icon: BarChart3 },
  ];
  return (
    <section className="py-20 md:py-28 relative">
      <div className="absolute inset-x-0 top-0 mx-auto w-[900px] h-[400px] blue-glow pointer-events-none" />
      <div className="container-auvra relative z-10">
        <div className="max-w-2xl mb-12">
          <Reveal><span className="kicker mb-3 block">Traction</span></Reveal>
          <Reveal i={1}>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-[1.05]">
              Real numbers. Real capital.<br /><span className="text-auvra-blue">Real distributions.</span>
            </h2>
          </Reveal>
          <Reveal i={2}>
            <p className="mt-4 text-secondary-auvra text-base md:text-lg leading-relaxed">AUVRA has been live since Phase 01. Below is a rolling snapshot of the ecosystem.</p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {M.map((m, i) => (
            <Reveal key={m.label} i={i}>
              <div className="glass rounded-2xl p-6 md:p-8 h-full">
                <m.icon className="w-5 h-5 text-auvra-blue mb-4" />
                <div className="text-3xl md:text-4xl font-semibold tracking-tight text-auvra-blue">
                  <AnimatedCounter value={m.v} suffix={m.s} />
                </div>
                <div className="mt-2 text-xs md:text-sm text-secondary-auvra uppercase tracking-wider">{m.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// =====================================================
//  SECURITY & COMPLIANCE STRIP
// =====================================================
export function SecurityAndCompliance() {
  const items = [
    { I: Fingerprint, t: "KYC / AML / KYB", d: "Onboarding across individuals, entities and beneficial owners. Continuous sanctions and PEP screening." },
    { I: ScrollText, t: "Legal Wrapper", d: "Jurisdiction-appropriate SPV or fund structure holding the underlying asset with clear token-holder rights." },
    { I: Lock, t: "Custody & Keys", d: "Multi-signature treasury management and institutional custody integrations across regions." },
    { I: Radio, t: "24/7 Monitoring", d: "Continuous risk scoring, anomaly detection and incident response with formal escalation." },
    { I: ShieldCheck, t: "Independent Audits", d: "Smart contract reviews, periodic attestations and transparent operational reporting." },
    { I: LayersIcon, t: "On-chain Transfer Rules", d: "Whitelisted transfers, jurisdictional restrictions and accreditation gating at the token layer." },
  ];
  return (
    <section className="py-20 md:py-28" id="security-compliance">
      <div className="container-auvra">
        <div className="max-w-2xl mb-12">
          <Reveal><span className="kicker mb-3 block">Trust Framework</span></Reveal>
          <Reveal i={1}>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-[1.05]">
              Security and compliance,<br /><span className="text-auvra-blue">built in — not bolted on.</span>
            </h2>
          </Reveal>
          <Reveal i={2}>
            <p className="mt-4 text-secondary-auvra text-base md:text-lg leading-relaxed">Every layer of the AUVRA platform — identity, custody, transfer, treasury and reporting — is designed to institutional standards.</p>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <Reveal key={it.t} i={i % 3}>
              <div className="glass rounded-2xl p-8 h-full flex gap-4 items-start hover:border-auvra-blue/30 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-auvra-blue/10 border border-auvra-blue/20 flex items-center justify-center flex-shrink-0">
                  <it.I className="w-5 h-5 text-auvra-blue" />
                </div>
                <div>
                  <h3 className="font-medium">{it.t}</h3>
                  <p className="text-sm text-secondary-auvra mt-2 leading-relaxed">{it.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal i={3}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/compliance" className="inline-flex items-center gap-2 border border-white/15 hover:border-auvra-blue text-white font-medium px-5 py-2.5 rounded-full text-sm transition-all">
              Compliance Framework <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/whitepaper" className="inline-flex items-center gap-2 text-sm text-auvra-blue hover:underline">
              Read the Whitepaper
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// =====================================================
//  FOR INSTITUTIONS — differentiators
// =====================================================
export function ForInstitutions() {
  const rows = [
    ["01", "Direct-relationship coverage", "Every institutional client is served by a named relationship manager with defined SLAs."],
    ["02", "Structured tickets", "Bespoke ticket sizes, tranche design, transfer restrictions and custody wiring for institutional flows."],
    ["03", "Reporting integration", "APIs, exports and periodic attestations that plug into your existing back-office and treasury systems."],
    ["04", "Cross-jurisdictional coverage", "AUVRA operates across EMEA, APAC and the Americas with jurisdiction-aware compliance flows."],
  ];
  return (
    <section className="py-20 md:py-28 relative" id="institutions">
      <div className="container-auvra">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-32">
            <Reveal><span className="kicker mb-3 block">For Institutions</span></Reveal>
            <Reveal i={1}>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-[1.05]">
                Built for the way institutions <span className="text-auvra-blue">actually operate</span>.
              </h2>
            </Reveal>
            <Reveal i={2}>
              <p className="mt-4 text-secondary-auvra text-base md:text-lg leading-relaxed">Not a retail app dressed up in a blazer. Real institutional workflows — from KYB and legal wrappers to structured tickets, custody and reporting integrations.</p>
            </Reveal>
            <Reveal i={3}>
              <Link to="/contact" className="mt-8 inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">
                Contact Institutional Desk <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-3 space-y-4">
            {rows.map(([n, t, d], i) => (
              <Reveal key={n} i={i}>
                <div className="glass rounded-2xl p-6 md:p-8 flex gap-6 items-start">
                  <div className="text-auvra-blue text-xs tracking-[0.2em] mt-1">{n}</div>
                  <div>
                    <h4 className="text-lg md:text-xl font-semibold tracking-tight">{t}</h4>
                    <p className="text-sm text-secondary-auvra mt-2 leading-relaxed">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// =====================================================
//  ECOSYSTEM PARTNERS strip
// =====================================================
export function EcosystemStrip() {
  const partners = ["BNB Smart Chain", "Chainlink", "Fireblocks", "Certik", "Elliptic", "Chainalysis", "Sumsub", "BitGo"];
  return (
    <section className="py-16 md:py-20 border-y border-white/5">
      <div className="container-auvra">
        <Reveal>
          <div className="text-center">
            <span className="kicker mb-6 block">Ecosystem & Infrastructure</span>
            <p className="text-secondary-auvra text-sm md:text-base max-w-2xl mx-auto">Integrated with best-in-class institutional infrastructure across custody, compliance, oracles and analytics.</p>
          </div>
        </Reveal>
        <Reveal i={1}>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="ecosystem-partners">
            {partners.map((p) => (
              <div key={p} className="glass rounded-xl px-3 py-4 text-center">
                <div className="text-xs md:text-sm font-medium">{p}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// =====================================================
//  REFERRAL BANNER — teaser for logged-out users
// =====================================================
export function ReferralBanner() {
  return (
    <section className="py-16 md:py-20">
      <div className="container-auvra">
        <div className="glass rounded-3xl p-8 md:p-12 relative overflow-hidden border border-auvra-blue/20">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] blue-glow pointer-events-none" />
          <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <Reveal><span className="kicker mb-3 block">Referral Program</span></Reveal>
              <Reveal i={1}>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tighter leading-[1.05]">
                  Refer a friend. <span className="text-auvra-blue">Earn 25 AUR each.</span>
                </h2>
              </Reveal>
              <Reveal i={2}>
                <p className="mt-4 text-secondary-auvra text-base leading-relaxed">Share your one-tap referral link. When your friend makes their first deposit, both of you are credited with 25 AUR — usable as spending credit against your next tokenized subscription.</p>
              </Reveal>
            </div>
            <Reveal i={2}>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Link to="/signup" className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">Get my referral code <ArrowUpRight className="w-4 h-4" /></Link>
                <Link to="/investors" className="inline-flex items-center gap-2 border border-white/15 hover:border-auvra-blue text-white font-medium px-6 py-3 rounded-full transition-all">Browse offerings</Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
